import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  BellIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useHelp } from '../contexts/HelpContext';
import { NavbarOfflineStatusDetailed } from './atoms/NavbarOfflineStatus.jsx';



const Navbar = ({ setSidebarOpen }) => {
  const { user, logout } = useAuth();
  const { showHelp } = useHelp();
  const location = useLocation();
  const navigate = useNavigate();

  const getHelpModalType = () => {
    switch (location.pathname) {
      case '/availability':
        return 'availability';
      case '/training-sessions':
        return 'training-sessions';
      case '/planner':
        return 'planner';
      case '/autoplanner':
        return 'autoplanner';
      case '/training-groups':
        return 'training-groups';
      case '/crews':
        return 'crews';
      default:
        return null;
    }
  };

  const handleHelpClick = () => {
    const modalType = getHelpModalType();
    if (modalType) {
      showHelp(modalType);
    } else {
      navigate('/help');
    }
  };

  return (
    <div className="sticky top-0 z-50 flex h-16 flex-shrink-0 bg-white shadow">
      <button
        type="button"
        className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-rowing-500 md:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      <div className="flex flex-1 justify-between px-4">
        <div className="flex flex-1">
          <div className="flex w-full md:ml-0">
            <div className="relative w-full text-gray-400 focus-within:text-gray-600">
              {/* You can add a search bar here if needed */}
            </div>
          </div>
        </div>

        <div className="ml-4 flex items-center md:ml-6">
          {/* Offline Status Indicator */}
          <NavbarOfflineStatusDetailed />

          {/* Help button - always available; opens context modal or Help Center */}
          <button
            type="button"
            onClick={handleHelpClick}
            className="mr-2 rounded-full bg-white p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            title="Get help"
          >
            <span className="sr-only">Get help</span>
            <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />
          </button>



          {/* Notifications */}
          <Link
            to="/notifications"
            className="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-rowing-500 focus:ring-offset-2"
          >
            <span className="sr-only">View notifications</span>
            <BellIcon className="h-6 w-6" aria-hidden="true" />
          </Link>

          {/* Profile dropdown */}
          <Menu as="div" className="relative z-[60] ml-3">
            <div>
              <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rowing-500 focus:ring-offset-2">
                <span className="sr-only">Open user menu</span>
                <div className="flex items-center space-x-2">
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  <span className="font-medium text-gray-700">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-[60] mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="/settings"
                      className={`${active ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-700`}
                    >
                      Settings
                    </a>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={logout}
                      className={`${active ? 'bg-gray-100' : ''} block w-full px-4 py-2 text-left text-sm text-gray-700`}
                    >
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
